import type {
  Content,
  ContentSvg,
  TableCell,
  TDocumentDefinitions,
  TFontDictionary,
} from "pdfmake/interfaces";
import type {
  BenefitsSummaryData,
  NetworkAmounts,
  ServiceCost,
} from "./benefitsSummary.types";

// Shared with the backend (backend/src/patientportal/benefitsSummaryPdf), so
// this file must stay free of browser APIs and runtime imports.

// Colors mirror the benefits summary page theme
const COLORS = {
  primary: "#10389a",
  primaryLight: "#e8f0fc",
  text: "#121212",
  muted: "#616161",
  border: "#e0e0e0",
  background: "#f5f5f5",
  success: "#2e7d32",
  successLight: "#e4f2e5",
  successText: "#1e4620",
  warning: "#ff9800",
  warningText: "#663c00",
  warningLight: "#fff4e5",
  chipWarning: "#e65100",
  chipWarningBg: "#ffe4cc",
  infoLight: "#e8f4fd",
};

const FIRST_VISITS_TEXT =
  "Your first few visits are usually a medical intake meeting or NeuroEvaluation. Both are medical visits, and they may not be scheduled yet. Some children go straight to therapy, and these visits do not apply.";
const WHAT_COMES_NEXT_TEXT =
  "After your first medical visit, your doctor may recommend services for your child, such as ABA, occupational therapy, or speech therapy. What you pay can be different for each service, as listed above. If a service needs pre-authorization from your insurance, Cortica handles that for you.";
const HOW_TO_READ_TEXT =
  "This summary is not a quote, and it does not guarantee what any visit will cost or what your plan will pay. What you pay depends on your plan's terms for each service, which services your care team recommends, how many hours are authorized, and how your insurance provider processes each claim. For the exact cost of a specific service, check your plan documents or call the number on the back of your insurance card.";
const DEDUCTIBLE_TEXT =
  "What you pay before your plan starts sharing the cost of covered care. Some plans apply it before a copay, so your first bill can differ from the amounts above.";
const OOP_TEXT =
  "The most your plan says you should pay in one plan year for covered care. Once you reach it, the plan pays covered costs for the rest of that year.";
const FAMILY_DEDUCTIBLE_TEXT =
  "Shared by everyone on the plan, so care for a sibling can count toward it.";
const FAMILY_OOP_TEXT =
  "The most everyone on the plan together should pay in one plan year for covered care.";

// A single-cell table gives us a filled, padded box like a MUI Paper/Alert
const box = (
  content: Content,
  fillColor: string,
  borderColor: string = fillColor,
): Content => ({
  table: { widths: ["*"], body: [[{ stack: [content] }]] },
  layout: {
    fillColor: () => fillColor,
    hLineColor: () => borderColor,
    vLineColor: () => borderColor,
    hLineWidth: () => 0.75,
    vLineWidth: () => 0.75,
    paddingLeft: () => 14,
    paddingRight: () => 14,
    paddingTop: () => 10,
    paddingBottom: () => 10,
  },
  margin: [0, 0, 0, 14],
});

// MUI icon paths (24x24) so the PDF carries the same icons as the page
const ICON_PATHS = {
  success:
    "M20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4C12.76,4 13.5,4.11 14.2,4.31L15.77,2.74C14.61,2.26 13.34,2 12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12M7.91,10.08L6.5,11.5L11,16L21,6L19.59,4.58L11,13.17L7.91,10.08Z",
  warning: "M12 5.99 19.53 19H4.47zM12 2 1 21h22zM13 16h-2v2h2zm0-6h-2v5h2z",
  info: "M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8",
  verified:
    "M23 11.99 20.56 9.2l.34-3.69-3.61-.82L15.4 1.5 12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 11.99l2.44 2.79-.34 3.7 3.61.82 1.89 3.2 3.4-1.47 3.4 1.46 1.89-3.19 3.61-.82-.34-3.69zm-3.95 1.48-.56.65.08.85.18 1.95-1.9.43-.84.19-.44.74-.99 1.68-1.78-.77-.8-.34-.79.34-1.78.77-.99-1.67-.44-.74-.84-.19-1.9-.43.18-1.96.08-.85-.56-.65L3.67 12l1.29-1.48.56-.65-.09-.86-.18-1.94 1.9-.43.84-.19.44-.74.99-1.68 1.78.77.8.34.79-.34 1.78-.77.99 1.68.44.74.84.19 1.9.43-.18 1.95-.08.85.56.65 1.29 1.47zM10.09 13.75l-2.32-2.33-1.48 1.49 3.8 3.81 7.34-7.36-1.48-1.49z",
};

const icon = (path: string, color: string, size = 16): ContentSvg => ({
  svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path fill="${color}" d="${path}"/></svg>`,
  width: size,
  height: size,
});

// Icon on the left, content on the right
const withIcon = (iconNode: ContentSvg, body: Content): Content => ({
  columns: [
    { ...iconNode, width: 16 },
    { width: "*", stack: [body] },
  ],
  columnGap: 10,
});

const sectionTitle = (text: string): Content => ({
  text,
  fontSize: 15,
  bold: true,
  color: COLORS.text,
  margin: [0, 0, 0, 2],
});

// MUI small chip: 24px tall, 8px side padding, 13px label (in pt here)
const CHIP_HEIGHT = 18;
const CHIP_PADDING_X = 6;
const CHIP_FONT_SIZE = 9.75;
// Label widths in Roboto 400 at CHIP_FONT_SIZE; pdfmake can't measure text
// before layout, so the pill is sized from these
const CHIP_LABEL_WIDTHS: Record<string, number> = {
  "In network": 45.33,
  "Out of network": 63.94,
};
// Puts the label's cap height in the vertical middle of the pill
const CHIP_TEXT_OFFSET_Y = 3.4;

// Rounded pill drawn on a canvas with the label laid over it
const chip = (label: string, color: string, background: string): Content => {
  const width =
    (CHIP_LABEL_WIDTHS[label] ?? label.length * CHIP_FONT_SIZE * 0.5) +
    CHIP_PADDING_X * 2;
  return {
    stack: [
      {
        canvas: [
          {
            type: "rect",
            x: 0,
            y: 0,
            w: width,
            h: CHIP_HEIGHT,
            r: CHIP_HEIGHT / 2,
            color: background,
          },
        ],
      },
      {
        text: label,
        color,
        fontSize: CHIP_FONT_SIZE,
        lineHeight: 1,
        relativePosition: {
          x: CHIP_PADDING_X,
          y: -CHIP_HEIGHT + CHIP_TEXT_OFFSET_Y,
        },
      },
    ],
  };
};

// No chip when the insurer did not say, matching the page's NetworkChip
const networkLabel = (network: ServiceCost["network"]): Content =>
  network === "in"
    ? chip("In network", COLORS.success, COLORS.successLight)
    : network === "out"
      ? chip("Out of network", COLORS.chipWarning, COLORS.chipWarningBg)
      : { text: "" };

const serviceRow = (service: ServiceCost): TableCell[] => [
  {
    stack: [
      { text: service.service, fontSize: 12, bold: true },
      ...(service.note
        ? [
            {
              text: service.note,
              fontSize: 10.5,
              color: COLORS.muted,
              margin: [0, 3, 0, 0] as [number, number, number, number],
            },
          ]
        : []),
    ],
  },
  networkLabel(service.network),
  {
    text: [
      {
        text: service.amount,
        fontSize: 15,
        font: "RobotoBold",
        color: service.selfPay ? COLORS.text : COLORS.primary,
      },
      ...(service.unit
        ? [
            {
              text: `${service.selfPay ? "," : ""} ${service.unit}`,
              fontSize: 10.5,
              color: COLORS.muted,
            },
          ]
        : []),
    ],
  },
];

const amountCell = (value: string, met?: string): TableCell =>
  met
    ? {
        stack: [
          { text: value, fontSize: 15, font: "RobotoBold" },
          { text: met, fontSize: 10.5, color: COLORS.muted, margin: [0, 2, 0, 0] },
        ],
      }
    : { text: value, fontSize: 15, font: "RobotoBold" };

const limitRow = (
  title: string,
  description: string,
  amounts: NetworkAmounts,
): TableCell[] => [
  {
    stack: [
      { text: title, fontSize: 12, bold: true },
      {
        text: description,
        fontSize: 10.5,
        color: COLORS.muted,
        margin: [0, 2, 0, 0],
      },
    ],
  },
  amountCell(amounts.inNetwork, amounts.inNetworkMet),
  amountCell(amounts.outOfNetwork, amounts.outOfNetworkMet),
];

const planFact = (
  label: string,
  value: string,
  description: string,
): Content => ({
  table: {
    widths: ["*"],
    body: [
      [
        {
          stack: [
            { text: label, fontSize: 10.5, color: COLORS.muted },
            { text: value, fontSize: 12, bold: true },
            { text: description, fontSize: 10.5, margin: [0, 3, 0, 0] },
          ],
        },
      ],
    ],
  },
  layout: {
    fillColor: () => COLORS.background,
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 10,
    paddingRight: () => 10,
    paddingTop: () => 8,
    paddingBottom: () => 8,
  },
});

// White card (like a MUI Paper) with one table row per section so it can
// break across pages between sections
const card = (
  sections: Content[],
  margin: [number, number, number, number] = [0, 0, 0, 0],
): Content => ({
  table: { widths: ["*"], body: sections.map((section) => [section]) },
  layout: {
    fillColor: () => "#ffffff",
    hLineWidth: (i: number, node: { table: { body: unknown[] } }) =>
      i === 0 || i === node.table.body.length ? 0.75 : 0,
    vLineWidth: () => 0.75,
    hLineColor: () => COLORS.border,
    vLineColor: () => COLORS.border,
    paddingLeft: () => 18,
    paddingRight: () => 18,
    paddingTop: (i: number) => (i === 0 ? 18 : 0),
    paddingBottom: (i: number, node: { table: { body: unknown[] } }) =>
      i === node.table.body.length - 1 ? 18 : 0,
  },
  margin,
});

const ruleLayout = {
  hLineColor: () => COLORS.border,
  vLineWidth: () => 0,
  hLineWidth: (i: number, node: { table: { body: unknown[] } }) =>
    i === 0 || i === node.table.body.length ? 0 : 0.75,
  paddingTop: () => 12,
  paddingBottom: () => 12,
  paddingLeft: () => 0,
  paddingRight: () => 8,
};

export const buildBenefitsSummaryDocument = (
  data: BenefitsSummaryData,
): TDocumentDefinitions => {
  const headerCell = (text: string) => ({
    text,
    fontSize: 10.5,
    color: COLORS.muted,
  });

  const insuranceField = (label: string, value: string): Content => ({
    stack: [
      { text: label, fontSize: 10.5, color: COLORS.muted },
      { text: value, fontSize: 12, bold: true, margin: [0, 2, 0, 0] },
    ],
  });

  const content: Content[] = [
    // Header
    { text: `Benefits summary for ${data.childName}`, fontSize: 18 },
    {
      text: `What your insurance provider told us about your plan on ${data.checkedOn}.`,
      fontSize: 10.5,
      color: COLORS.muted,
      margin: [0, 4, 0, 16],
    },

    // Insurance details
    box(
      {
        stack: [
          {
            columns: [
              {
                text: "Insurance details",
                fontSize: 15,
                bold: true,
                width: "*",
              },
              {
                width: "auto",
                columns: [
                  {
                    ...icon(ICON_PATHS.verified, COLORS.success, 13),
                    width: 13,
                  },
                  {
                    width: "auto",
                    text: `Benefits checked ${data.checkedOn}`,
                    fontSize: 9.75,
                    color: COLORS.success,
                    margin: [0, 1, 0, 0],
                  },
                ],
                columnGap: 4,
              },
            ],
            margin: [0, 0, 0, 10],
          },
          {
            columns: [
              insuranceField("Insurance provider", data.insurance.provider),
              insuranceField("Plan name", data.insurance.planName),
              insuranceField("Member ID", data.insurance.memberId),
              insuranceField("Policyholder", data.insurance.policyholder),
            ],
            columnGap: 10,
          },
        ],
      },
      "#ffffff",
      COLORS.border,
    ),
  ];

  if (data.outOfPocketMaxMetNote) {
    content.push(
      box(
        withIcon(icon(ICON_PATHS.success, COLORS.success, 18), {
          text: data.outOfPocketMaxMetNote,
          fontSize: 10.5,
          color: COLORS.successText,
        }),
        COLORS.successLight,
      ),
    );
  }

  if (data.referralRequired) {
    content.push(
      box(
        withIcon(icon(ICON_PATHS.warning, COLORS.warning, 18), {
          stack: [
            {
              text: "Referral needed",
              bold: true,
              fontSize: 12,
              color: COLORS.warningText,
            },
            {
              text: "Your plan requires a referral before we can schedule. A member of our team will contact you to walk you through the steps.",
              fontSize: 10.5,
              color: COLORS.warningText,
              margin: [0, 2, 0, 0],
            },
          ],
        }),
        COLORS.warningLight,
      ),
    );
  }

  // Plan details card
  const planSections: Content[] = [
    sectionTitle("Plan details"),
    {
      text: "What your plan lists for each service.",
      fontSize: 10.5,
      color: COLORS.muted,
      margin: [0, 0, 0, 10],
    },
    box(
      withIcon(icon(ICON_PATHS.info, COLORS.primary, 18), {
        stack: [
          {
            text: "Your first few visits",
            bold: true,
            color: COLORS.primary,
            fontSize: 10.5,
          },
          { text: FIRST_VISITS_TEXT, fontSize: 10.5, margin: [0, 2, 0, 0] },
        ],
      }),
      COLORS.primaryLight,
    ),
    {
      table: {
        headerRows: 1,
        widths: ["*", 150, 230],
        dontBreakRows: true,
        body: [
          [
            headerCell("Service"),
            headerCell("Network"),
            headerCell("Your cost"),
          ],
          ...data.services.map(serviceRow),
        ],
      },
      layout: ruleLayout,
      margin: [0, 0, 0, 12],
    },
  ];

  if (data.sharedLimitNote) {
    planSections.push(
      box({ text: data.sharedLimitNote, fontSize: 10.5 }, COLORS.infoLight),
    );
  }

  // Same rows and order as the page's PlanLimits
  const hasFamily = !!(data.familyDeductible || data.familyOutOfPocketMax);
  const limitRows: TableCell[][] = [
    limitRow(
      hasFamily ? "Annual deductible, individual" : "Annual deductible",
      DEDUCTIBLE_TEXT,
      data.deductible,
    ),
    ...(data.familyDeductible
      ? [
          limitRow(
            "Annual deductible, family",
            FAMILY_DEDUCTIBLE_TEXT,
            data.familyDeductible,
          ),
        ]
      : []),
    limitRow(
      hasFamily ? "Out-of-pocket maximum, individual" : "Out-of-pocket maximum",
      OOP_TEXT,
      data.outOfPocketMax,
    ),
    ...(data.familyOutOfPocketMax
      ? [
          limitRow(
            "Out-of-pocket maximum, family",
            FAMILY_OOP_TEXT,
            data.familyOutOfPocketMax,
          ),
        ]
      : []),
  ];

  // Kept together so the limits table never splits across pages
  planSections.push({
    unbreakable: true,
    stack: [
      {
        text: "Deductible and out-of-pocket maximum",
        fontSize: 12,
        bold: true,
        margin: [0, 8, 0, 6],
      },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: ["*", 80, 80],
          body: [
            [
              { text: "" },
              headerCell("In network"),
              headerCell("Out of network"),
            ],
            ...limitRows,
          ],
        },
        layout: ruleLayout,
        margin: [0, 0, 0, 8],
      },
    ],
  });

  // The out-of-network note stays with the plan facts so it never sits alone
  // at the bottom of a page
  planSections.push({
    unbreakable: true,
    stack: [
      // Spacer keeps it off the card edge when it starts a new page
      // (pdfmake drops top margins at a page break)
      {
        canvas: [{ type: "rect", x: 0, y: 0, w: 1, h: 14, color: "#ffffff" }],
      },
      ...(data.outOfNetworkNote
        ? [
            {
              text: data.outOfNetworkNote,
              fontSize: 10.5,
              color: COLORS.muted,
              margin: [0, 0, 0, 10] as [number, number, number, number],
            },
          ]
        : []),
      {
        columns: [
          planFact(
            "Plan year",
            data.planYear,
            "The deductible and out-of-pocket maximum start over when a new plan year begins.",
          ),
          // Left out when both levels are shown; the empty column keeps
          // plan year at half width like the page
          data.appliesTo
            ? planFact(
                "Applies to",
                data.appliesTo,
                data.appliesTo === "Family"
                  ? "Family limits are shared by everyone on the plan, so care for a sibling can count toward them."
                  : "These limits apply only to your child's care.",
              )
            : { text: "" },
        ],
        columnGap: 12,
      },
    ],
  });

  content.push(card(planSections, [0, 0, 0, 14]));

  // Guidance: two equal-height cards side by side
  content.push(
    {
      unbreakable: true,
      table: {
        widths: ["*", 14, "*"],
        body: [
          [
            {
              stack: [
                sectionTitle("What comes next"),
                {
                  text: WHAT_COMES_NEXT_TEXT,
                  fontSize: 10.5,
                  margin: [0, 6, 0, 0],
                },
              ],
            },
            { text: "" },
            {
              stack: [
                sectionTitle("How to read this summary"),
                {
                  text: HOW_TO_READ_TEXT,
                  fontSize: 10.5,
                  margin: [0, 6, 0, 0],
                },
              ],
            },
          ],
        ],
      },
      layout: {
        // The middle column is a gap showing the page background
        fillColor: (_row: number, _node: unknown, col?: number) =>
          col === 1 ? null : "#ffffff",
        hLineWidth: (_i: number, _node: unknown, col?: number) =>
          col === 1 ? 0 : 0.75,
        vLineWidth: () => 0.75,
        hLineColor: () => COLORS.border,
        vLineColor: () => COLORS.border,
        paddingLeft: (i: number) => (i === 1 ? 0 : 14),
        paddingRight: (i: number) => (i === 1 ? 0 : 14),
        paddingTop: () => 14,
        paddingBottom: () => 14,
      },
      margin: [0, 0, 0, 14],
    },
    // Contact
    box(
      {
        stack: [
          { text: "Questions about this summary?", bold: true, fontSize: 12 },
          {
            text: `Message our benefits team or call ${data.benefitsTeamPhone}.`,
            fontSize: 10.5,
            color: COLORS.muted,
            margin: [0, 2, 0, 0],
          },
        ],
      },
      "#ffffff",
      COLORS.border,
    ),
    {
      text: `Information as of ${data.checkedAt}. Source: your insurance provider.`,
      fontSize: 9,
      color: COLORS.muted,
    },
  );

  return {
    info: { title: `Benefits summary - ${data.childName}` },
    // Sized like the page at desktop width (1200px = 900pt) so proportions match
    pageSize: { width: 900, height: 1165 },
    pageMargins: [24, 28, 24, 44],
    defaultStyle: {
      font: "Roboto",
      fontSize: 10.5,
      lineHeight: 1.4,
      color: COLORS.text,
    },
    content,
    background: (
      _page: number,
      pageSize: { width: number; height: number },
    ) => ({
      canvas: [
        {
          type: "rect",
          x: 0,
          y: 0,
          w: pageSize.width,
          h: pageSize.height,
          color: COLORS.background,
        },
      ],
    }),
    footer: (currentPage: number, pageCount: number) => ({
      text: `Cortica  |  Page ${currentPage} of ${pageCount}`,
      alignment: "center",
      fontSize: 9,
      color: COLORS.muted,
      margin: [0, 16, 0, 0],
    }),
  };
};

// Roboto weights the layout uses. The browser download and the backend both
// embed @fontsource/roboto's files for these, so the PDF matches the page.
export type BenefitsPdfFontWeight = 400 | 500 | 700;
export const BENEFITS_PDF_FONT_WEIGHTS: BenefitsPdfFontWeight[] = [400, 500, 700];

// `fileFor` returns how pdfmake finds the font for a weight: a virtual file
// name in the browser, a file path on the backend.
export const benefitsPdfFonts = (
  fileFor: (weight: BenefitsPdfFontWeight) => string,
): TFontDictionary => ({
  // bold: true renders as Medium (500), matching the page's headings
  Roboto: {
    normal: fileFor(400),
    bold: fileFor(500),
    italics: fileFor(400),
    bolditalics: fileFor(500),
  },
  RobotoBold: {
    normal: fileFor(700),
    bold: fileFor(700),
    italics: fileFor(700),
    bolditalics: fileFor(700),
  },
});

export const benefitsSummaryPdfFileName = (data: BenefitsSummaryData): string =>
  `benefits-summary-${data.childName.replace(/[^\w]+/g, "-")}.pdf`;
